import { useMemo, useState } from 'react'
import { ClipboardCheck, ClipboardList, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { EventsSubTabs } from '@/components/events/EventsSubTabs'
import { DebriefModal } from '@/components/events/DebriefModal'
import { DISCIPLINE_LABELS, EventFormModal } from '@/components/events/EventFormModal'
import { EventScheduleImport } from '@/components/events/EventScheduleImport'
import { useDeleteEvent, useEvents, useSyncEvent, type EventResponse } from '@/hooks/useEvents'
import { fmtDate, todayIso } from '@/lib/dates'

const money = (v: number) =>
  v ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'

function syncStatus(e: EventResponse): string {
  if (e.sync_error) return 'Sync error'
  if (e.synced_at) return `Synced ${fmtDate(e.synced_at)} (${e.products_synced})`
  return 'Not synced'
}

export default function EventsAdmin() {
  const { data: events = [], isLoading } = useEvents()
  const deleteEvent = useDeleteEvent()
  const syncEvent = useSyncEvent()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<EventResponse | null>(null)
  const [syncingId, setSyncingId] = useState<number | null>(null)
  const [syncAll, setSyncAll] = useState<{ done: number; total: number } | null>(null)
  const [debriefOpen, setDebriefOpen] = useState(false)
  const [debriefing, setDebriefing] = useState<EventResponse | null>(null)

  async function onSync(e: EventResponse) {
    setSyncingId(e.id)
    try {
      const res = await syncEvent.mutateAsync(e.id)
      const warn = res.warnings.length ? ` (${res.warnings.length} warning(s))` : ''
      toast.success(
        `Synced ${e.location_name} ${fmtDate(e.event_date)}: ${res.products_synced} products from ` +
        `${res.transfers_found} transfer(s)${warn}`
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed')
    } finally {
      setSyncingId(null)
    }
  }

  // Sync every past event sequentially, oldest-first — the order the
  // carryover chain requires (each event reads the previous same-location
  // event's stored leftover). Future events are skipped: nothing has
  // happened at them yet.
  async function onSyncAll() {
    const today = todayIso()
    const targets = [...events]
      .filter((e) => e.event_date <= today)
      .sort((a, b) => a.event_date.localeCompare(b.event_date) || a.id - b.id)
    if (targets.length === 0) {
      toast.info('No past events to sync')
      return
    }
    if (!window.confirm(
      `Sync ${targets.length} past event(s) from Shopify, oldest first? ` +
      'This takes a few seconds per event — leave this page open until it finishes.'
    )) return

    setSyncAll({ done: 0, total: targets.length })
    const failures: string[] = []
    try {
      for (const e of targets) {
        setSyncingId(e.id)
        try {
          await syncEvent.mutateAsync(e.id)
        } catch (err) {
          failures.push(`${e.location_name} ${fmtDate(e.event_date)}: ${err instanceof Error ? err.message : 'failed'}`)
        }
        setSyncAll((p) => (p ? { ...p, done: p.done + 1 } : p))
      }
    } finally {
      setSyncingId(null)
      setSyncAll(null)
    }
    if (failures.length === 0) {
      toast.success(`Synced all ${targets.length} past event(s)`)
    } else {
      toast.error(
        `Synced ${targets.length - failures.length} of ${targets.length} — failed: ${failures.join('; ')}`,
        { duration: 10000 }
      )
    }
  }

  const grouped = useMemo(() => {
    const byLocation = new Map<string, EventResponse[]>()
    for (const e of events) {
      const key = e.location_name ?? String(e.shopify_location_id)
      const list = byLocation.get(key) ?? []
      list.push(e)
      byLocation.set(key, list)
    }
    // Events arrive date-DESC from the API; show each location's weeks in
    // chronological order like a season calendar.
    return [...byLocation.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, list]) => [name, [...list].sort((a, b) => a.event_date.localeCompare(b.event_date))] as const)
  }, [events])

  function openCreate() {
    setEditing(null)
    setModalOpen(true)
  }

  function openEdit(e: EventResponse) {
    setEditing(e)
    setModalOpen(true)
  }

  async function onDelete(e: EventResponse) {
    const label = `${e.location_name} ${fmtDate(e.event_date)}`
    if (!window.confirm(`Delete event "${label}"? Synced sales data for it is removed too.`)) return
    try {
      await deleteEvent.mutateAsync(e.id)
      toast.success(`Deleted ${label}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      <div className="flex-1 overflow-auto p-4 max-w-5xl w-full mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <h1 className="text-base font-semibold">Event Admin</h1>
          <span className="ml-2 inline-block px-2 py-0.5 text-xs border border-[var(--line-warm)] bg-[var(--surface-card)] rounded">
            Registered: <strong>{events.length}</strong>
          </span>
          <div className="flex-1" />
          <EventsSubTabs />
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={onSyncAll}
            disabled={syncingId != null || syncAll != null}
            title="Sync every past event from Shopify, oldest first — the order the carryover chain needs"
          >
            <RefreshCw className={`mr-1 h-3 w-3 ${syncAll ? 'animate-spin' : ''}`} />
            {syncAll ? `Syncing ${syncAll.done + 1}/${syncAll.total}…` : 'Sync all'}
          </Button>
          <Button size="sm" className="h-7 text-xs" onClick={openCreate}>
            <Plus className="mr-1 h-3 w-3" />
            New event
          </Button>
        </div>
        <p className="text-xs text-[var(--mute)] mb-3">
          Register event weeks, set disciplines, and enter event costs. Booth fee, labor, and
          travel are optional but required for profitability reporting.
        </p>

        <div className="mb-4">
          <EventScheduleImport />
        </div>

        {isLoading ? (
          <div className="space-y-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : grouped.length === 0 ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-6 text-center text-xs text-[var(--mute)]">
            No events registered. Upload the season schedule or click “New event”.
          </div>
        ) : (
          grouped.map(([locationName, list]) => (
            <div key={locationName} className="mb-4">
              <h2 className="text-sm font-semibold mb-1">{locationName}</h2>
              <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)]">
                <table className="text-xs w-full">
                  <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
                    <tr className="text-left text-[var(--mute)]">
                      <th className="tbl-th">Date</th>
                      <th className="tbl-th text-right">Days</th>
                      <th className="tbl-th">Discipline</th>
                      <th className="tbl-th text-right">Booth</th>
                      <th className="tbl-th text-right">Labor</th>
                      <th className="tbl-th text-right">Travel</th>
                      <th className="tbl-th">Sync</th>
                      <th className="tbl-th w-16" />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((e, i) => (
                      <tr key={e.id} className={`border-b border-[var(--line-warm)] ${i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'}`}>
                        <td className="px-2 py-1 tabular-nums whitespace-nowrap">{fmtDate(e.event_date)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{e.event_duration_days}</td>
                        <td className="px-2 py-1">
                          <span className="inline-block px-1.5 py-0.5 border border-[var(--line-warm)] rounded">
                            {DISCIPLINE_LABELS[e.discipline] ?? e.discipline}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(e.booth_fee)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(e.labor_cost)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{money(e.travel_lodging)}</td>
                        <td
                          className={`px-2 py-1 ${e.sync_error ? 'text-[var(--status-rejected-fg)]' : 'text-[var(--mute)]'}`}
                          title={e.sync_error ?? undefined}
                        >
                          {syncStatus(e)}
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex gap-1 justify-end">
                            <button
                              aria-label="Sync event from Shopify"
                              title="Sync transfers + POS sales from Shopify"
                              onClick={() => onSync(e)}
                              disabled={syncingId != null}
                              className="p-1 hover:text-[var(--text-primary)] disabled:opacity-40"
                            >
                              <RefreshCw className={`h-3 w-3 ${syncingId === e.id ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                              aria-label="Post-event debrief"
                              title={
                                e.debrief_completed_at
                                  ? `Debrief completed ${fmtDate(e.debrief_completed_at)}`
                                  : 'Fill post-event debrief'
                              }
                              onClick={() => {
                                setDebriefing(e)
                                setDebriefOpen(true)
                              }}
                              className="p-1 hover:text-[var(--text-primary)]"
                            >
                              {e.debrief_completed_at ? (
                                <ClipboardCheck className="h-3 w-3 text-[var(--status-approved-fg)]" />
                              ) : (
                                <ClipboardList className="h-3 w-3" />
                              )}
                            </button>
                            <button
                              aria-label="Edit event"
                              onClick={() => openEdit(e)}
                              className="p-1 hover:text-[var(--text-primary)]"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button
                              aria-label="Delete event"
                              onClick={() => onDelete(e)}
                              className="p-1 hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      <EventFormModal open={modalOpen} onOpenChange={setModalOpen} event={editing} />
      <DebriefModal open={debriefOpen} onOpenChange={setDebriefOpen} event={debriefing} />
    </div>
  )
}
